import { useCallback, useEffect, useState } from "react";

import PropTypes from "prop-types";

import { Delete as DeleteIcon, KeyboardArrowDown } from "@mui/icons-material";
import {
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";

import { CustomerSelect } from "@/features/customers";
import {
  AddPromotionCodeFormDialog,
  BulkUploadPromotionCodesDialog,
} from "@/features/promotion-codes";
import { PromotionTypeEnum } from "@/features/promotions";
import { apiClient, formatNumber, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle, SearchTextField, useSnackbar } from "@/features/ui";
import { utils, writeFile } from "xlsx";

export const PromotionCodesSection = ({ promotion }) => {
  const promotionId = promotion.id;
  const { showSnackbar } = useSnackbar();

  const [promotionCodes, setPromotionCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [customerFilter, setCustomerFilter] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [deleteDialogCode, setDeleteDialogCode] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = useState(null);

  const loadPromotionCodes = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get(`promotions/${promotionId}/promotion-codes`, {
        params: {
          page: paginationModel.page,
          size: paginationModel.pageSize,
          code: searchQuery || undefined,
          customerId: customerFilter?.id || undefined,
          withPromotionRedemptionCount: true,
        },
      });
      setPromotionCodes(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error),
      );
    } finally {
      setLoading(false);
    }
  }, [
    promotionId,
    paginationModel.page,
    paginationModel.pageSize,
    searchQuery,
    customerFilter,
    showSnackbar,
  ]);

  useEffect(() => {
    loadPromotionCodes();
  }, [loadPromotionCodes]);

  const handleDelete = async () => {
    if (!deleteDialogCode) return;

    try {
      setIsDeleting(true);
      await apiClient.delete(`promotion-codes/${deleteDialogCode.id}`);
      showSnackbar("Kode promosi berhasil dihapus", "success");
      setDeleteDialogCode(null);
      loadPromotionCodes();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal menghapus kode promosi"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearchQueryChange = debounce((event) => {
    setSearchQuery(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, 500);

  const handleDownloadTemplate = () => {
    const ws = utils.json_to_sheet([{ customerId: "" }]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Template");
    writeFile(wb, "template-kode-promosi.xlsx");
    setBulkMenuAnchorEl(null);
  };

  const isPublic = promotion?.promotionType === PromotionTypeEnum.PUBLIC;
  const isTargeted = promotion?.promotionType === PromotionTypeEnum.TARGETED;
  const showAddButton = isTargeted || (isPublic && rowCount === 0);

  const columns = [
    {
      field: "code",
      headerName: "Kode Promo",
      flex: 2,
      sortable: false,
    },
    ...(isTargeted
      ? [
          {
            field: "customerName",
            headerName: "Pelanggan",
            flex: 2,
            sortable: false,
            valueGetter: (_value, row) => row.customer?.name,
          },
        ]
      : []),
    {
      field: "promotionRedemptionsCount",
      headerName: "Jumlah Redeem",
      flex: 1,
      sortable: false,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      minWidth: 80,
      getActions: (params) => [
        <IconButton
          key="delete"
          onClick={() => setDeleteDialogCode(params.row)}
          size="small"
          disabled={params.row.promotionRedemptionsCount > 0}
        >
          <DeleteIcon />
        </IconButton>,
      ],
    },
  ];

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Kode Promosi
      </Typography>

      <Stack spacing={2}>
        <Grid container spacing={2} sx={{ justifyContent: "space-between" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack direction="row" spacing={2}>
              {showAddButton && (
                <Button
                  variant="contained"
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  Tambah Kode
                </Button>
              )}
              {isTargeted && (
                <>
                  <Button
                    variant="outlined"
                    endIcon={<KeyboardArrowDown />}
                    onClick={(e) => setBulkMenuAnchorEl(e.currentTarget)}
                  >
                    Upload Bulk
                  </Button>
                  <Menu
                    anchorEl={bulkMenuAnchorEl}
                    open={Boolean(bulkMenuAnchorEl)}
                    onClose={() => setBulkMenuAnchorEl(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setBulkMenuAnchorEl(null);
                        setIsBulkDialogOpen(true);
                      }}
                    >
                      Upload File
                    </MenuItem>
                    <MenuItem onClick={handleDownloadTemplate}>
                      Download Template
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Stack>
          </Grid>
          {isTargeted && (
            <Grid size={{ xs: 12, md: 3 }}>
              <SearchTextField
                fullWidth
                placeholder="Cari kode promosi"
                onChange={handleSearchQueryChange}
              />
            </Grid>
          )}
          {isTargeted && (
            <Grid size={{ xs: 12, md: 3 }}>
              <CustomerSelect
                value={customerFilter}
                onChange={(value) => {
                  setCustomerFilter(value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              />
            </Grid>
          )}
        </Grid>

        <DataGrid
          rows={promotionCodes}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
        />
      </Stack>

      <AddPromotionCodeFormDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSubmitted={loadPromotionCodes}
        promotionId={promotionId}
        promotionType={promotion?.promotionType}
      />

      {isTargeted && (
        <BulkUploadPromotionCodesDialog
          isOpen={isBulkDialogOpen}
          onClose={() => setIsBulkDialogOpen(false)}
          onSubmitted={loadPromotionCodes}
          promotionId={promotionId}
        />
      )}

      {!!deleteDialogCode && (
        <Dialog
          open
          onClose={() => !isDeleting && setDeleteDialogCode(null)}
          maxWidth="xs"
          fullWidth
        >
          <CustomDialogTitle
            title="Hapus Kode Promosi"
            onClose={() => !isDeleting && setDeleteDialogCode(null)}
          />
          <DialogContent>
            <DialogContentText>
              Apakah Anda yakin ingin menghapus kode promosi{" "}
              <strong>{deleteDialogCode.code}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogCode(null)}
              disabled={isDeleting}
            >
              Batal
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {isDeleting ? "Menghapus..." : "Hapus"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
};

PromotionCodesSection.propTypes = {
  promotion: PropTypes.object.isRequired,
};
